import {getCurrentUserLogin} from '@github-ui/client-env'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getPersisterStorageKey} from '@github-ui/react-query-persister/storage-key'
import safeStorage from '@github-ui/safe-storage'

export type IssueViewerFeed = 'comments' | 'activity'
export type IssueViewerStorageCoordinates = {owner: string; repo: string; number: number; feed: IssueViewerFeed}

/** `AVAILABLE`: the viewer turned the preview off and can turn it back on from this issue. */
export type IssueViewerActivityTimelineState = 'ENABLED' | 'AVAILABLE' | 'UNAVAILABLE'
export type IssueViewerActivityTimeline = {state: IssueViewerActivityTimelineState; canToggle: boolean}

export const ISSUE_VIEWER_PERSISTER_PREFIX = 'server-persisted-query'
export const ISSUE_VIEWER_CACHE_TTL_MS = 1000 * 60 * 60 * 24 * 14
export const ISSUE_VIEWER_DEFAULT_TIMELINE_PAGE_SIZE = 15
export const ISSUE_VIEWER_EXPANDED_TIMELINE_PAGE_SIZE = 25

type ActivityTimelineChoice = 'on' | 'off'

const ACTIVITY_TIMELINE_CHOICE_KEY_PREFIX = 'issue-viewer:activity-timeline-choice'
const safeLocalStorage = safeStorage('localStorage')

// Picks the cached snapshot to restore before the server says which feed it chose. The flag
// doesn't know the viewer's own choice, so that is remembered from toggles and fresh responses.
export function getIssueViewerFeed(): IssueViewerFeed {
  const choice = getActivityTimelineChoice()
  const enabled = choice ? choice === 'on' : isFeatureEnabled('issue_viewer_activity_tab')
  return enabled ? 'comments' : 'activity'
}

/** Remembers turning the preview on or off, which applies to every repository. */
export function rememberIssueViewerActivityTimelineChoice(enabled: boolean) {
  setActivityTimelineChoice(enabled ? 'on' : 'off')
}

/** `ENABLED` can come from an organization rollout rather than the viewer, so it only clears an opt-out. */
export function rememberIssueViewerActivityTimelineState(state: IssueViewerActivityTimelineState | undefined) {
  const choice = getActivityTimelineChoice()
  if (state === 'AVAILABLE') {
    setActivityTimelineChoice('off')
  } else if ((state === 'ENABLED' && choice === 'off') || (state === 'UNAVAILABLE' && choice === 'on')) {
    setActivityTimelineChoice(undefined)
  }
}

function getActivityTimelineChoice(): ActivityTimelineChoice | undefined {
  const key = activityTimelineChoiceKey()
  const choice = key ? safeLocalStorage.getItem(key) : null
  return choice === 'on' || choice === 'off' ? choice : undefined
}

function setActivityTimelineChoice(choice: ActivityTimelineChoice | undefined) {
  const key = activityTimelineChoiceKey()
  if (!key) return

  if (choice) {
    safeLocalStorage.setItem(key, choice)
  } else {
    safeLocalStorage.removeItem(key)
  }
}

function activityTimelineChoiceKey(): string | undefined {
  const login = getCurrentUserLogin()
  return login ? `${ACTIVITY_TIMELINE_CHOICE_KEY_PREFIX}:${login}` : undefined
}

export function getIssueViewerInitialTimelinePageSize(): number {
  return isFeatureEnabled('issue_viewer_activity_tab')
    ? ISSUE_VIEWER_EXPANDED_TIMELINE_PAGE_SIZE
    : ISSUE_VIEWER_DEFAULT_TIMELINE_PAGE_SIZE
}

export function issueViewerFeedFromTimelineItemTypes(
  timelineItemTypes: readonly string[] | null | undefined,
): IssueViewerFeed {
  return timelineItemTypes?.length === 1 && timelineItemTypes[0] === 'ISSUE_COMMENT' ? 'comments' : 'activity'
}

export function timelineItemTypesForIssueViewerFeed(feed: IssueViewerFeed): readonly ['ISSUE_COMMENT'] | undefined {
  return feed === 'comments' ? ['ISSUE_COMMENT'] : undefined
}

export function getIssueViewerStorageQueryKey({
  owner,
  repo,
  number,
  feed,
}: IssueViewerStorageCoordinates): readonly unknown[] {
  // Activity keeps the pre-split key so persisted entries and the service worker lookup stay valid.
  return feed === 'comments'
    ? ['issue-viewer', 'primary-view', {number, owner, repo, feed}]
    : ['issue-viewer', 'primary-view', {number, owner, repo}]
}

export function getIssueViewerPersisterStorageKey(coordinates: IssueViewerStorageCoordinates): string {
  return getPersisterStorageKey({
    prefix: ISSUE_VIEWER_PERSISTER_PREFIX,
    queryKey: getIssueViewerStorageQueryKey(coordinates),
  })
}
